/**
 * useCrewDraft — the local staged-draft state for the CrewSlotEditor (BON-200).
 * Owns the working crew list, the dirty diff vs. the saved set, add/remove,
 * cancel (revert) and commit (one onSave with the full set). Keeps the editor
 * component small. The draft re-seeds from the server when the saved crew
 * changes — but only while clean, so an in-flight edit is never clobbered.
 */
import { useMemo, useState } from 'react';

import type { CrewPosition, CrewSlotInput, WorkshopCrewMember } from '../../../../api/workshops';

import { draftFromCrew, isDirty, toPayload, type DraftMember } from './crewModel';

export function useCrewDraft(
  staff: WorkshopCrewMember[],
  onSave: (staff: CrewSlotInput[]) => Promise<unknown>,
) {
  const saved = useMemo(() => draftFromCrew(staff), [staff]);
  const [draft, setDraft] = useState<DraftMember[]>(saved);
  const [error, setError] = useState<string | null>(null);

  const savedSig = saved.map((m) => `${m.position}:${m.userId}`).join('|');
  const [lastSig, setLastSig] = useState(savedSig);
  if (savedSig !== lastSig && !isDirty(draft, staff)) {
    setLastSig(savedSig);
    setDraft(saved);
  }

  const remove = (userId: string) => setDraft((d) => d.filter((m) => m.userId !== userId));
  const add = (member: { userId: string; name: string }, position: CrewPosition) =>
    setDraft((d) => [...d, { ...member, position }]);
  const cancel = () => {
    setDraft(saved);
    setError(null);
  };
  const commit = async () => {
    setError(null);
    try {
      await onSave(toPayload(draft));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save crew.');
    }
  };

  return {
    draft,
    dirty: isDirty(draft, staff),
    error,
    excludedUserIds: draft.map((m) => m.userId),
    add,
    remove,
    cancel,
    commit,
  };
}
